import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, FileText, GitBranch, Search } from 'lucide-react';
import { OwnerAvatar, type RepoSummary } from './gitShared';
import { VoiceDictationControl } from './VoiceDictationControl';

// CommandPalette is the app-wide keyboard launcher. It is mounted once (in
// SiteNav) so `/`, `t`, and Cmd/Ctrl+K open it from anywhere. It searches your
// repos, the showcase repos, and the top-level pages, then full-navigates on
// Enter. Full-screen tools can mark their shell with [data-owns-keys] so local
// keymaps own bare-letter shortcuts; Cmd/Ctrl+K remains the universal launcher.

interface PageEntry {
  type: 'page';
  label: string;
  href: string;
  keywords: string;
}

interface RepoEntry {
  type: 'repo';
  label: string; // owner/name
  sub: string;
  href: string;
  avatar?: string;
  owner: string;
}

type Entry = PageEntry | RepoEntry;

const PAGES: PageEntry[] = [
  { type: 'page', label: 'Repos', href: '/repos', keywords: 'repositories git source code browse' },
  { type: 'page', label: 'Search', href: '/search', keywords: 'web repos pages voice command palette' },
  { type: 'page', label: 'Pull requests', href: '/pulls', keywords: 'prs review merge inbox' },
  { type: 'page', label: 'Mail', href: '/mail', keywords: 'email inbox superhuman compose transactional designer agent filters' },
  { type: 'page', label: 'Projects', href: '/projects', keywords: 'workspace' },
  { type: 'page', label: 'Agent', href: '/agent', keywords: 'coding agent studio tasks goal' },
  { type: 'page', label: 'Integrations', href: '/integrations', keywords: 'slack teams jira linear notion github gitlab azure devops bitbucket jetbrains xcode deeplinks sdk cloud agent meta agent' },
  { type: 'page', label: 'RL training', href: '/rl', keywords: 'reinforcement learning ppo dqn grpo gymnasium atari mujoco rlhf policy gpu train' },
  { type: 'page', label: 'Enterprise', href: '/enterprise', keywords: 'teams rbac sso saml scim audit logs spend limits soc2' },
  { type: 'page', label: 'Settings', href: '/settings', keywords: 'profile workspace members roles invites privacy spend limit region audit' },
  { type: 'page', label: 'Social scheduler', href: '/social', keywords: 'posts schedule twitter x linkedin reddit bluesky mastodon calendar queue' },
  { type: 'page', label: 'Skills marketplace', href: '/skills/market', keywords: 'mcp servers prompt packs install desktop agent tools' },
  { type: 'page', label: 'Chat', href: '/chat', keywords: 'ai assistant research web search' },
  { type: 'page', label: 'Gateway', href: '/gateway', keywords: 'api models proxy playground' },
  { type: 'page', label: 'Worlds', href: '/worlds', keywords: 'panorama 360 vr environment hunyuan metahuman' },
  { type: 'page', label: 'Models', href: '/models', keywords: 'catalog pricing compare' },
  { type: 'page', label: 'Tools', href: '/tools', keywords: 'prompt optimizer evals compare' },
  { type: 'page', label: 'AI Use Cases', href: '/ai', keywords: 'staging headshot voiceover restore product photo sticker logo tattoo music sfx video directory' },
  { type: 'page', label: 'Deploys', href: '/deploys', keywords: 'builds ship release' },
  { type: 'page', label: 'Build Studio', href: '/builds', keywords: 'container image registry docker build' },
  { type: 'page', label: 'CI Control Room', href: '/ci', keywords: 'pipeline github actions buildkite circleci gitlab builds' },
  { type: 'page', label: 'Media Optimizer', href: '/media-optimizer', keywords: 'image cdn resize responsive av1 video reencode transform' },
  { type: 'page', label: 'Assistants', href: '/assistants', keywords: 'characters chat voice' },
  { type: 'page', label: 'Group Chats', href: '/group-chat', keywords: 'characters room multi ai conversation' },
  { type: 'page', label: 'Characters', href: '/characters', keywords: 'gallery roleplay' },
  { type: 'page', label: 'Art', href: '/art', keywords: 'image search gallery' },
  { type: 'page', label: 'Write', href: '/write', keywords: 'docs document editor rich text docx' },
  { type: 'page', label: 'Sheets', href: '/sheets', keywords: 'spreadsheet xlsx csv table' },
  { type: 'page', label: 'Draw', href: '/draw', keywords: 'whiteboard excalidraw diagram sketch' },
  { type: 'page', label: 'Fusion', href: '/fusion', keywords: 'fusion model panel compare judge synthesize multi-model best answer openrouter' },
  { type: 'page', label: 'Diffusion Canvas', href: '/diffusion', keywords: 'image generate inpaint outpaint expand infinite canvas playground' },
  { type: 'page', label: 'Live Draw', href: '/livedraw', keywords: 'sketch render realtime krea img2img pen pressure' },
  { type: 'page', label: 'Music', href: '/music', keywords: 'audio sfx sound generate song' },
  { type: 'page', label: 'Audio editor', href: '/audio', keywords: 'waveform trim fade record wav mp3 podcast' },
  { type: 'page', label: 'Image editor', href: '/image', keywords: 'photo raster layers crop brush photopea' },
  { type: 'page', label: 'Slides', href: '/slides', keywords: 'presentation deck pptx powerpoint canva' },
  { type: 'page', label: 'Vector editor', href: '/vector', keywords: 'svg illustrator inkscape pen bezier shapes' },
  { type: 'page', label: 'Video studio', href: '/studio', keywords: 'video timeline edit render' },
  { type: 'page', label: '3D editor', href: '/3d', keywords: 'three model mesh glb obj' },
  { type: 'page', label: 'AnimFlow', href: '/animflow', keywords: 'animation keyframe timeline tween onion skin layers ai motion git webm sprite sheet' },
  { type: 'page', label: 'Manim Agent', href: '/manim', keywords: 'math explainer python animation video code tts character wasm gallery prompt' },
  { type: 'page', label: 'Creation Feed', href: '/feed', keywords: 'social creators tutorials code models datasets artifacts follow likes' },
  { type: 'page', label: 'Artifacts', href: '/artifacts', keywords: 'library files share' },
  { type: 'page', label: 'Pricing', href: '/pricing', keywords: 'credits cost plans' },
  { type: 'page', label: 'Blog', href: '/blog', keywords: 'posts writing' },
  { type: 'page', label: 'Investors', href: '/investors', keywords: 'investors vision deep dive company fundraising' },
  { type: 'page', label: 'Addons', href: '/addons', keywords: 'postgres postgis pgvector gobed vector search database mongo memcache auth monitor hosted' },
  { type: 'page', label: 'Docs', href: '/docs', keywords: 'documentation help' },
  { type: 'page', label: 'Account', href: '/account', keywords: 'settings credits billing topup' },
];

// `g` then one of these navigates (GitHub-style chords). Listed in the `?` help.
const NAV_CHORDS: Array<{ key: string; label: string; href: string }> = [
  { key: 'h', label: 'Home', href: '/' },
  { key: 'r', label: 'Repos', href: '/repos' },
  { key: 'p', label: 'Pull requests', href: '/pulls' },
  { key: 'e', label: 'Mail', href: '/mail' },
  { key: 'a', label: 'Agent', href: '/agent' },
  { key: 'c', label: 'Chat', href: '/chat' },
  { key: 'i', label: 'Art (images)', href: '/art' },
  { key: 'w', label: 'Write (docs)', href: '/write' },
  { key: 's', label: 'Sheets', href: '/sheets' },
  { key: 'd', label: 'Draw', href: '/draw' },
  { key: 'm', label: 'Music', href: '/music' },
  { key: 'g', label: 'Gateway', href: '/gateway' },
  { key: 'l', label: 'Artifacts library', href: '/artifacts' },
  { key: 'u', label: 'Account / usage', href: '/account' },
];

const CHORD_TIMEOUT_MS = 1500;

const KEY_OWNER_SELECTOR = '[data-owns-keys], [data-repo-shell]';
const KEY_OWNER_PATHS = new Set([
  '/studio', '/video', '/3d-editor', '/3d', '/3d-studio', '/animflow', '/animation',
  '/audio', '/audio-editor', '/image', '/image-editor', '/photo',
  '/slides', '/presentation', '/vector', '/svg-editor',
]);

function hasLocalBareKeyOwner(): boolean {
  if (document.querySelector(KEY_OWNER_SELECTOR)) return true;
  const path = window.location.pathname.replace(/\/$/, '') || '/';
  return KEY_OWNER_PATHS.has(path) || path.startsWith('/repos/');
}

function score(text: string, q: string): number {
  if (!q) return 0;
  const t = text.toLowerCase();
  const query = q.toLowerCase();
  const idx = t.indexOf(query);
  if (idx >= 0) return 1000 - idx - t.length * 0.05; // substring: strong, prefer early/short
  // subsequence fallback
  let ti = 0;
  let hits = 0;
  for (let qi = 0; qi < query.length; qi++) {
    const at = t.indexOf(query[qi], ti);
    if (at < 0) return -Infinity;
    ti = at + 1;
    hits++;
  }
  return hits - t.length * 0.05;
}

export const CommandPalette: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [repos, setRepos] = useState<RepoEntry[]>([]);
  const pendingChordAt = useRef(0);
  const loaded = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const loadRepos = useCallback(async () => {
    if (loaded.current) return;
    loaded.current = true;
    const toEntry = (r: RepoSummary): RepoEntry => ({
      type: 'repo',
      label: r.fullName,
      sub: r.description || '',
      href: `/repos/${r.fullName}`,
      avatar: r.ownerAvatar,
      owner: r.owner,
    });
    const [mine, explore] = await Promise.all([
      fetch('/api/repos').then((r) => r.json()).catch(() => null),
      fetch('/api/repos/explore').then((r) => r.json()).catch(() => null),
    ]);
    const seen = new Set<string>();
    const out: RepoEntry[] = [];
    for (const body of [mine, explore]) {
      for (const r of (body?.repos || []) as RepoSummary[]) {
        if (seen.has(r.fullName)) continue;
        seen.add(r.fullName);
        out.push(toEntry(r));
      }
    }
    setRepos(out);
  }, []);

  const openPalette = useCallback(() => {
    setOpen(true);
    setQuery('');
    setActive(0);
    loadRepos();
  }, [loadRepos]);

  // Global hotkeys. Cmd/Ctrl+K everywhere; `/` and `t` everywhere except where
  // a mounted app shell declares ownership of bare-letter shortcuts.
  useEffect(() => {
    const isTyping = (el: EventTarget | null) => {
      const t = el as HTMLElement | null;
      return !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
    };
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        openPalette();
        return;
      }
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTyping(e.target)) return;
      if (e.key === 'Escape') {
        setHelpOpen(false);
        return;
      }
      if (hasLocalBareKeyOwner()) return;
      if (e.key === '?') {
        e.preventDefault();
        setHelpOpen((o) => !o);
        return;
      }
      if (pendingChordAt.current && Date.now() - pendingChordAt.current < CHORD_TIMEOUT_MS) {
        pendingChordAt.current = 0;
        const chord = NAV_CHORDS.find((c) => c.key === e.key);
        if (chord) {
          e.preventDefault();
          window.location.href = chord.href;
          return;
        }
      }
      if (e.key === 'g') {
        pendingChordAt.current = Date.now();
        return;
      }
      if (e.key !== '/' && e.key !== 't') return;
      e.preventDefault();
      openPalette();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openPalette]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const results = useMemo<Entry[]>(() => {
    const all: Entry[] = [...repos, ...PAGES];
    if (!query.trim()) {
      // Default view: pages first (fast nav), then a few repos.
      return [...PAGES, ...repos].slice(0, 40);
    }
    const scored = all
      .map((e) => {
        const hay = e.type === 'repo' ? `${e.label} ${e.sub}` : `${e.label} ${e.keywords}`;
        return { e, s: score(hay, query.trim()) };
      })
      .filter((x) => x.s > -Infinity);
    scored.sort((a, b) => b.s - a.s);
    return scored.slice(0, 40).map((x) => x.e);
  }, [query, repos]);

  useEffect(() => {
    setActive((a) => Math.min(a, Math.max(0, results.length - 1)));
  }, [results.length]);

  const go = useCallback((entry: Entry | undefined) => {
    if (!entry) return;
    setOpen(false);
    window.location.href = entry.href;
  }, []);

  const onListKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(results[active]);
    }
  };

  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-idx="${active}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  if (!open) {
    if (!helpOpen) return null;
    return (
      <div
        className="pointer-events-auto fixed inset-0 z-[200] flex items-start justify-center bg-black/60 p-4 pt-[12vh] backdrop-blur-sm"
        onClick={() => setHelpOpen(false)}
      >
        <div
          className="w-full max-w-md overflow-hidden rounded-xl border border-white/12 bg-[#0d1117] shadow-2xl shadow-black/60"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
            <span className="text-sm font-medium text-white">Keyboard shortcuts</span>
            <kbd className="rounded bg-white/10 px-1.5 py-0.5 text-[10px] text-white/40">esc</kbd>
          </div>
          <div className="max-h-[60vh] overflow-y-auto px-4 py-3 text-sm">
            <div className="mb-3 space-y-1.5">
              {[
                ['⌘K / Ctrl+K', 'Command palette (anywhere)'],
                ['/ or t', 'Command palette'],
                ['?', 'This help'],
              ].map(([k, d]) => (
                <div key={k} className="flex items-center justify-between gap-4">
                  <span className="text-white/72">{d}</span>
                  <kbd className="shrink-0 rounded bg-white/10 px-1.5 py-0.5 text-[11px] text-white/60">{k}</kbd>
                </div>
              ))}
            </div>
            <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-white/34">Go to — press g then…</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
              {NAV_CHORDS.map((c) => (
                <div key={c.key} className="flex items-center justify-between gap-2">
                  <span className="truncate text-white/72">{c.label}</span>
                  <kbd className="shrink-0 rounded bg-white/10 px-1.5 py-0.5 text-[11px] text-white/60">g {c.key}</kbd>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="pointer-events-auto fixed inset-0 z-[200] flex items-start justify-center bg-black/60 p-4 pt-[12vh] backdrop-blur-sm"
      onClick={() => setOpen(false)}
    >
      <div
        className="w-full max-w-xl overflow-hidden rounded-xl border border-white/12 bg-[#0d1117] shadow-2xl shadow-black/60"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-white/10 px-3">
          <Search size={16} className="text-white/40" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            onKeyDown={onListKey}
            placeholder="Search repos and pages…"
            className="h-12 min-w-0 flex-1 bg-transparent text-sm text-white outline-none placeholder:text-white/34"
          />
          <VoiceDictationControl
            value={query}
            onChange={(next) => {
              setQuery(next);
              setActive(0);
            }}
            ariaLabel="Dictate search"
            buttonClassName="h-9 w-9"
          />
          <kbd className="hidden rounded bg-white/10 px-1.5 py-0.5 text-[10px] text-white/40 sm:block">esc</kbd>
        </div>
        <div ref={listRef} className="max-h-[52vh] overflow-y-auto py-1">
          {results.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-white/40">No matches.</p>
          ) : (
            results.map((entry, i) => (
              <button
                key={`${entry.type}:${entry.href}`}
                data-idx={i}
                type="button"
                onMouseEnter={() => setActive(i)}
                onClick={() => go(entry)}
                className={`flex w-full items-center gap-3 px-3 py-2 text-left text-sm ${
                  i === active ? 'bg-white/10 text-white' : 'text-white/72 hover:bg-white/[0.06]'
                }`}
              >
                {entry.type === 'repo' ? (
                  <OwnerAvatar name={entry.owner} src={entry.avatar} size={20} />
                ) : (
                  <FileText size={18} className="shrink-0 text-white/40" />
                )}
                <span className="flex min-w-0 flex-col">
                  <span className="flex items-center gap-1.5 truncate font-medium">
                    {entry.type === 'repo' && <GitBranch size={12} className="shrink-0 text-white/34" />}
                    {entry.label}
                  </span>
                  {entry.type === 'repo' && entry.sub && (
                    <span className="truncate text-xs text-white/40">{entry.sub}</span>
                  )}
                </span>
                <ArrowRight size={14} className={`ml-auto shrink-0 ${i === active ? 'text-white/60' : 'text-transparent'}`} />
              </button>
            ))
          )}
        </div>
        <div className="flex items-center gap-3 border-t border-white/10 px-3 py-2 text-[11px] text-white/34">
          <span><kbd className="rounded bg-white/10 px-1">↑↓</kbd> navigate</span>
          <span><kbd className="rounded bg-white/10 px-1">↵</kbd> open</span>
          <span className="ml-auto"><kbd className="rounded bg-white/10 px-1">⌘K</kbd> / <kbd className="rounded bg-white/10 px-1">/</kbd></span>
        </div>
      </div>
    </div>
  );
};
